import { Capacitor } from '@capacitor/core';
import { CameraAlt, Delete, Download, Edit } from '@mui/icons-material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { format as formatTz, toZonedTime } from 'date-fns-tz';
import React, { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Lightbox } from 'react-modal-image';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { EntityGrid } from '@sophocles/data-grids';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import {
  addSitePhoto,
  deleteCameraImage,
  deleteSitePhoto,
  editSitePhoto,
  loadCameraImages,
  loadSitePhotos,
} from '@sophocles/features/media/api';
import {
  selectMediaViewProperties,
  selectPhotosBySiteId,
  setImagesMode,
  setImagesTab,
} from '@sophocles/features/media/slice';
import { addToast } from '@sophocles/features/toast';
import { userPermCheck } from '@sophocles/utils';
import ImageGallery from '@sophocles/views/sites/components/media/ImageGallery';
import ImageUploadDialog from '@sophocles/views/sites/dialogs/ImageUploadDialog';
import PhotoEditDialog from '@sophocles/views/sites/dialogs/PhotoEditDialog';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

// Fixed visibility per grid variant — column set is driven by the descriptor;
// these hide the fields not relevant to each view type.
const CIC_COLUMN_VISIBILITY = { model: { uploaded_by: false, comment: false }, onChange: () => {} };
const PHOTOS_COLUMN_VISIBILITY = { model: { camera_number: false, camera_description: false }, onChange: () => {} };

function MediaView(props) {
  const dispatch = useDispatch();
  const dropZoneRef = createRef();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const dateFnsLocale = i18n.language?.startsWith('es') ? es : enUS;

  const isNativePlatform = Capacitor.isNativePlatform();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isImageUploadDialogOpen, setIsImageUploadDialogOpen] = useState(false);
  const [isPhotoEditDialogOpen, setIsPhotoEditDialogOpen] = useState(false);
  const [isLightBoxOpen, setIsLightBoxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const viewProperties = useSelector(selectMediaViewProperties);
  const { selectedStructure: selectedSite, siteId, loading } = useSiteDashboardLayoutContext();

  // Derive hasCamera and cic reactively from selectedSite so tab visibility is
  // always correct regardless of when the component mounts relative to the
  // structure data being seeded.
  const cic = useMemo(() => {
    const standAloneCic = selectedSite.get('cic', null);
    const addonCIC = selectedSite.getIn(['relay_controller', 'cic'], null);
    return standAloneCic != null ? standAloneCic : addonCIC;
  }, [selectedSite]);

  const hasCamera = cic != null;

  // Photos from Redux — still needed for ImageGallery (gallery mode).
  const photos = useSelector(selectPhotosBySiteId(siteId));
  const { user } = useUser();

  const canAddImages = userPermCheck(user, 'pliny.add_sitephoto');
  const canEditPhotos = userPermCheck(user, 'pliny.change_sitephoto');
  const canDeletePhotos = userPermCheck(user, 'pliny.delete_sitephoto');
  const canDeleteImages = userPermCheck(user, 'pliny.delete_cicimage');

  // Server-paginated grids don't observe redux mutations, so a delete must
  // refetch the grid explicitly. EntityGrid hands back its page-0 refresh via
  // onGridReady; capture it per grid and call it after a confirmed delete.
  const photosGrid = useGridRefreshRef();
  const cicGrid = useGridRefreshRef();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleEditPhoto = useCallback(photo => {
    setSelectedPhoto(photo);
    setIsPhotoEditDialogOpen(true);
  }, []);

  // Delete goes through a confirmation dialog — prevents accidental deletes and
  // the double-click that previously 404'd on the already-removed row.
  const handleDeletePhoto = useCallback(guid => setDeleteTarget({ guid, kind: 'photo' }), []);
  const handleDeleteImage = useCallback(guid => setDeleteTarget({ guid, kind: 'image' }), []);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    const { guid, kind } = deleteTarget;
    const thunk = kind === 'photo' ? deleteSitePhoto : deleteCameraImage;
    const refreshGrid = kind === 'photo' ? photosGrid.refresh : cicGrid.refresh;
    dispatch(thunk(guid)).then(action => {
      const ok = action.meta?.requestStatus === 'fulfilled';
      // Refetch page 0 so the server-paginated grid drops the removed row.
      refreshGrid();
      dispatch(
        addToast({
          title: ok ? t('general.success') : t('general.error', 'Error'),
          message: ok ? t('media.imageDeletedSuccess') : t('media.imageDeleteFailed', 'Failed to delete image.'),
          level: ok ? 'success' : 'error',
          dismissible: true,
        }),
      );
    });
    setDeleteTarget(null);
  }, [deleteTarget, dispatch, t, photosGrid.refresh, cicGrid.refresh]);

  // Fetch functions for the two EntityGrid instances. Memoized so the grid's
  // dataSource is only recreated when the site changes, not on every render.
  const cicFetchFn = useCallback(
    params => dispatch(loadCameraImages({ siteId, queryParams: params })),
    [dispatch, siteId],
  );

  const photosFetchFn = useCallback(
    params => dispatch(loadSitePhotos({ siteId, queryParams: params })),
    [dispatch, siteId],
  );

  // Field overrides shared by both grids: image thumbnail (opens lightbox),
  // tz-aware timestamp, and camera_number formatted as "Camera N".
  const fieldOverrides = useMemo(
    () => ({
      file_url: {
        renderCell: ({ value }) => (
          <img
            src={value}
            alt={t('media.uploadedImage')}
            style={{ width: '100%', maxWidth: '300px', cursor: 'pointer' }}
            onClick={() => {
              setSelectedImage(value);
              setIsLightBoxOpen(true);
            }}
          />
        ),
      },
      timestamp: {
        renderCell: ({ row }) => {
          const utcDate = new Date(row.timestamp);
          const userTimeZone = selectedSite.get('time_zone', 'America/Chicago');
          const userDate = toZonedTime(utcDate, userTimeZone);
          const relativeTime = formatDistanceToNow(userDate, { addSuffix: true, locale: dateFnsLocale });
          const formattedDateWithTime = formatTz(userDate, 'yyyy-MM-dd hh:mm a', { timeZone: userTimeZone });
          return (
            <>
              <Typography variant="caption">{formattedDateWithTime}</Typography>
              <br />
              <small>({relativeTime})</small>
            </>
          );
        },
      },
      camera_number: {
        renderCell: ({ value }) => {
          if (value == null) return null;
          return <Typography variant="caption">{t('media.cameraNumberLabel', { number: value })}</Typography>;
        },
      },
      uploaded_by: {
        renderCell: ({ value }) => {
          if (!value) return null;
          // Tolerates both the current full user object and a future
          // { id, type, name } user ref.
          const label = value.name ?? `${value.first_name ?? ''} ${value.last_name ?? ''}`.trim();
          return <Typography variant="caption">{label || value.email || '—'}</Typography>;
        },
      },
    }),
    [selectedSite, dateFnsLocale, t],
  );

  // Actions column for CIC images: Delete (permission-gated) + Download.
  const cicActionsColumn = useMemo(
    () => [
      {
        field: '__actions',
        headerName: t('actions.actions'),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        hideable: false,
        width: 160,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {canDeleteImages && (
              <Tooltip title={t('actions.deleteImage', { defaultValue: 'Delete Image' })}>
                <IconButton size="small" color="error" onClick={() => handleDeleteImage(row.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('actions.downloadImage', { defaultValue: 'Download Image' })}>
              <a href={row.file_url} target="_blank" rel="noopener noreferrer">
                <IconButton size="small" color="primary">
                  <Download fontSize="small" />
                </IconButton>
              </a>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [canDeleteImages, handleDeleteImage, t],
  );

  // Actions column for site photos: Edit + Delete (both permission-gated) + Download.
  const photosActionsColumn = useMemo(
    () => [
      {
        field: '__actions',
        headerName: t('actions.actions'),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        hideable: false,
        width: 160,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {canEditPhotos && (
              <Tooltip title={t('actions.editDetails', { defaultValue: 'Edit Details' })}>
                <IconButton size="small" color="primary" onClick={() => handleEditPhoto(row)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDeletePhotos && (
              <Tooltip title={t('actions.deleteImage', { defaultValue: 'Delete Image' })}>
                <IconButton size="small" color="error" onClick={() => handleDeletePhoto(row.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('actions.downloadImage', { defaultValue: 'Download Image' })}>
              <a href={row.file_url} target="_blank" rel="noopener noreferrer">
                <IconButton size="small" color="primary">
                  <Download fontSize="small" />
                </IconButton>
              </a>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [canEditPhotos, canDeletePhotos, handleEditPhoto, handleDeletePhoto, t],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    let currentTab = viewProperties.tab;
    const requestedTab = params.get('tab');
    if (requestedTab != null && requestedTab !== currentTab) {
      currentTab = requestedTab;
      dispatch(setImagesTab(currentTab));
    }

    const requestedMode = params.get('mode');
    if (requestedMode != null && requestedMode !== viewProperties.mode && ['grid', 'list'].includes(requestedMode)) {
      dispatch(setImagesMode(requestedMode));
    }

    // If the current tab is 'images' but there's no camera, reset to photos.
    if (!hasCamera && currentTab === 'images') {
      dispatch(setImagesTab('photos'));
    }

    setIsLoading(false);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', pb: 'var(--space-3)' }}>
        <TabContext value={(!hasCamera && viewProperties.tab == 'photos') || hasCamera ? viewProperties.tab : 'photos'}>
          {hasCamera && (
            <Box sx={{ mb: 'var(--space-3)' }}>
              <TabList
                onChange={(e, newTab) => dispatch(setImagesTab(newTab))}
                aria-label={t('media.mediaTabs')}
                sx={{
                  minHeight: 'auto',
                  width: 'fit-content',
                  bgcolor: 'var(--surface-secondary)',
                  borderRadius: 'var(--radius-md)',
                  p: '4px',
                  '& .MuiTabs-indicator': { display: 'none' },
                  '& .MuiTab-root': {
                    minHeight: 32,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-sm)',
                    px: 2,
                  },
                  '& .Mui-selected': {
                    bgcolor: 'var(--md-surface-container-lowest)',
                    color: 'text.primary',
                    boxShadow: 'var(--shadow-sm)',
                  },
                }}
              >
                <Tab label={t('media.images')} value="images" />
                <Tab label={t('media.photos')} value="photos" />
              </TabList>
            </Box>
          )}

          <Box>
            {hasCamera && (
              <TabPanel value="images" sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '70vh', minHeight: 360 }}>
                  <EntityGrid
                    basePath="binary-files"
                    fieldOverrides={fieldOverrides}
                    fetchFn={cicFetchFn}
                    options={{
                      infiniteScroll: true,
                      showSearch: false,
                      columnVisibility: CIC_COLUMN_VISIBILITY,
                      extraColumns: cicActionsColumn,
                      onGridReady: cicGrid.onGridReady,
                    }}
                  />
                </Box>
              </TabPanel>
            )}
            <Dropzone
              multiple
              ref={c => (dropZoneRef.current = c)}
              style={{ width: '100%', height: '100%' }}
              activeStyle={{ border: '3px dashed var(--border-default)', margin: '-3px' }}
              disableClick={true}
            >
              {({ getRootProps, getInputProps }) => (
                <TabPanel value="photos" sx={{ p: 0 }}>
                  {isLoading ? (
                    <Skeleton variant="rectangular" width="100%" height={300} />
                  ) : (
                    <div {...getRootProps()} style={{ width: '100%', height: '100%', overflow: 'auto' }}>
                      {viewProperties.mode === 'list' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '70vh', minHeight: 360 }}>
                          <EntityGrid
                            basePath="binary-files"
                            fieldOverrides={fieldOverrides}
                            fetchFn={photosFetchFn}
                            options={{
                              infiniteScroll: true,
                              showSearch: false,
                              columnVisibility: PHOTOS_COLUMN_VISIBILITY,
                              extraColumns: photosActionsColumn,
                              onGridReady: photosGrid.onGridReady,
                              createAction:
                                canAddImages && !isNativePlatform
                                  ? {
                                      label: t('actions.upload'),
                                      onClick: () => setIsImageUploadDialogOpen(true),
                                    }
                                  : undefined,
                            }}
                          />
                        </Box>
                      ) : (
                        <>
                          {canAddImages && !isNativePlatform && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                              <Button
                                variant="contained"
                                disabled={isImageUploadDialogOpen}
                                onClick={() => setIsImageUploadDialogOpen(true)}
                              >
                                {t('actions.upload')}
                              </Button>
                            </Box>
                          )}
                          <ImageGallery
                            images={photos}
                            onDelete={canDeletePhotos ? handleDeletePhoto : null}
                            onEdit={canEditPhotos ? handleEditPhoto : null}
                          />
                        </>
                      )}
                    </div>
                  )}
                </TabPanel>
              )}
            </Dropzone>
          </Box>
        </TabContext>
        {isNativePlatform && canAddImages && (
          <Fab
            color="primary"
            aria-label={t('actions.save')}
            disabled={isImageUploadDialogOpen}
            onClick={() => setIsImageUploadDialogOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 16,
            }}
          >
            <CameraAlt />
          </Fab>
        )}
      </Box>
      {canEditPhotos && selectedPhoto && (
        <PhotoEditDialog
          open={isPhotoEditDialogOpen}
          photo={selectedPhoto}
          onSubmit={data => {
            setIsLoading(true);
            dispatch(editSitePhoto({ id: selectedPhoto.guid, comment: data.comment }))
              .then(() => {
                setIsPhotoEditDialogOpen(false);
                setSelectedPhoto(null);
              })
              .finally(() => {
                setIsLoading(false);
              });
          }}
          onClose={() => {
            setIsPhotoEditDialogOpen(false);
            setSelectedPhoto(null);
          }}
        />
      )}
      {canAddImages && (
        <ImageUploadDialog
          open={isImageUploadDialogOpen}
          onDelete={canDeletePhotos ? handleDeletePhoto : undefined}
          onSubmit={data => {
            data.forEach(datum => {
              const fd = new FormData();
              fd.append('file', datum.file);
              fd.append('entity_type', 'site');
              fd.append('entity_id', siteId);
              fd.append('file_type', 'photo');
              fd.append('comment', datum.comment);
              setIsLoading(true);
              dispatch(addSitePhoto(fd))
                .then(() => {
                  setIsImageUploadDialogOpen(false);
                })
                .finally(() => {
                  setIsLoading(false);
                });
            });
          }}
          onClose={() => setIsImageUploadDialogOpen(false)}
        />
      )}
      {isLightBoxOpen && (
        <Lightbox medium={selectedImage} large={selectedImage} onClose={() => setIsLightBoxOpen(false)} />
      )}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('media.deleteConfirmTitle', 'Delete Image')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('media.deleteConfirmText', 'Are you sure you want to delete this image? This cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('actions.cancel')}</Button>
          <Button color="error" onClick={confirmDelete}>
            {t('actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MediaView;
